import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  RefreshCw,
  Users,
  Clock,
  AlertTriangle,
  Lock,
  Sparkles,
  ChevronRight,
  Check,
  X,
  Edit2,
  Trash2,
  ArrowRight,
  FileText,
  Settings,
  AlertCircle,
  MoreVertical,
  Scissors,
  User,
  HelpCircle,
  Info
} from 'lucide-react';
import { cn } from '../lib/utils';
import { teeSheetRulesAPI, playerAPI } from '../services/api';
import RuleDetailSheet from '../components/RuleDetailSheet';
import './TournamentRules.css';

// Rule type display config
const RULE_TYPE_CONFIG = {
  partner_group: { icon: Users, label: 'Partner Groups', color: 'blue', description: 'Players who want to play together' },
  avoid_partner: { icon: X, label: 'Avoid Partner', color: 'red', description: 'Players who should not be grouped' },
  time_constraint: { icon: Clock, label: 'Time Constraints', color: 'orange', description: 'Hard time requirements (must be satisfied)' },
  time_preference: { icon: Clock, label: 'Time Preferences', color: 'yellow', description: 'Soft time preferences (best effort)' },
  handicap_sort: { icon: Sparkles, label: 'Handicap Sort', color: 'purple', description: 'Sort players by handicap index' },
  pace_balance: { icon: Sparkles, label: 'Pace Balance', color: 'green', description: 'Balance pace of play in groups' },
  manual_override: { icon: Lock, label: 'Manual Override', color: 'gray', description: 'Director manual placements' },
  group_lock: { icon: Lock, label: 'Group Lock', color: 'indigo', description: 'Locked group configurations' },
  slot_lock: { icon: Lock, label: 'Slot Lock', color: 'indigo', description: 'Players locked to specific slots' }
};

// Status display config
const STATUS_CONFIG = {
  pending: { variant: 'secondary', label: 'Pending', description: 'Not yet processed' },
  applied: { variant: 'success', label: 'Applied', description: 'Successfully applied' },
  partial: { variant: 'warning', label: 'Partial', description: 'Partially satisfied' },
  failed: { variant: 'destructive', label: 'Failed', description: 'Could not be satisfied' },
  overridden: { variant: 'outline', label: 'Overridden', description: 'Superseded by higher priority rule' },
  conflict: { variant: 'destructive', label: 'Conflict', description: 'Has unresolved conflicts' }
};

// Flexibility display config
const FLEXIBILITY_CONFIG = {
  hard: { label: 'Required', variant: 'destructive' },
  soft: { label: 'Preferred', variant: 'secondary' },
  flexible: { label: 'Flexible', variant: 'outline' }
};

function TournamentRules() {
  const { tournamentId } = useParams();
  const navigate = useNavigate();
  const { tournament, refreshCounts } = useOutletContext() || {};

  const [rules, setRules] = useState([]);
  const [grouped, setGrouped] = useState({});
  const [stats, setStats] = useState({ total: 0, pending: 0, applied: 0, conflicts: 0, failed: 0 });
  const [conflicts, setConflicts] = useState([]);
  const [playerCount, setPlayerCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [expandedTypes, setExpandedTypes] = useState(['partner_group', 'time_constraint', 'time_preference']);
  const [selectedRule, setSelectedRule] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  // Fetch rules from API
  const fetchRules = useCallback(async () => {
    if (!tournamentId) return;

    setIsLoading(true);
    setError(null);
    try {
      const response = await teeSheetRulesAPI.getRules(tournamentId);
      if (response.data.success) {
        setRules(response.data.rules);
        setGrouped(response.data.grouped);
        setStats(response.data.stats);
      }
    } catch (err) {
      console.error('Error fetching rules:', err);
      setError('Failed to load rules');
    } finally {
      setIsLoading(false);
    }
  }, [tournamentId]);

  // Fetch conflicts
  const fetchConflicts = useCallback(async () => {
    if (!tournamentId) return;

    try {
      const response = await teeSheetRulesAPI.getConflicts(tournamentId);
      if (response.data.success) {
        setConflicts(response.data.conflicts);
      }
    } catch (err) {
      console.error('Error fetching conflicts:', err);
    }
  }, [tournamentId]);

  // Fetch player count
  const fetchPlayerCount = useCallback(async () => {
    if (!tournamentId) return;
    try {
      const response = await playerAPI.getAll(tournamentId);
      setPlayerCount(response.data?.length || 0);
    } catch (err) {
      console.error('Error fetching player count:', err);
    }
  }, [tournamentId]);

  // Extract rules from player data
  const handleExtractRules = async () => {
    if (!tournamentId) return;

    setIsExtracting(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const response = await teeSheetRulesAPI.extractRules(tournamentId, {
        clearExisting: true,
        includeSystemDefaults: true
      });

      if (response.data.success) {
        setSuccessMessage(`Extracted ${response.data.stats?.total || response.data.rules?.length || 0} rules from ${playerCount} players`);
        await fetchRules();
        await fetchConflicts();
        setTimeout(() => setSuccessMessage(null), 5000);
      }
    } catch (err) {
      console.error('Error extracting rules:', err);
      setError(err.response?.data?.error || 'Failed to extract rules');
    } finally {
      setIsExtracting(false);
    }
  };

  // Delete a rule
  const handleDeleteRule = async (ruleId) => {
    try {
      await teeSheetRulesAPI.deleteRule(ruleId);
      await fetchRules();
      setIsDetailOpen(false);
      setSelectedRule(null);
    } catch (err) {
      console.error('Error deleting rule:', err);
      setError('Failed to delete rule');
    }
  };

  // Open rule detail sheet
  const handleOpenRuleDetail = (rule) => {
    setSelectedRule(rule);
    setIsDetailOpen(true);
  };

  // Save rule updates
  const handleSaveRule = async (updatedRule) => {
    try {
      await teeSheetRulesAPI.updateRule(updatedRule._id, {
        priority: updatedRule.priority,
        flexibility: updatedRule.flexibility,
      });
      await fetchRules();
      setSuccessMessage('Rule updated successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error updating rule:', err);
      setError('Failed to update rule');
    }
  };

  // Split a group into pairs
  const handleSplitGroup = async (rule) => {
    if (!rule || rule.players?.length <= 2) return;

    try {
      // For now, just delete the original rule and show a message
      // In production, this would create multiple new rules
      const playerNames = rule.playerNames || [];
      await teeSheetRulesAPI.deleteRule(rule._id);

      // Create two new partner groups from the split
      const midpoint = Math.ceil(playerNames.length / 2);
      const group1Players = rule.players.slice(0, midpoint);
      const group2Players = rule.players.slice(midpoint);

      // Create first group
      if (group1Players.length > 1) {
        await teeSheetRulesAPI.createRule(tournamentId, {
          type: 'partner_group',
          players: group1Players,
          flexibility: rule.flexibility,
          priority: rule.priority,
          source: 'director_override',
          parameters: { splitFrom: rule._id }
        });
      }

      // Create second group
      if (group2Players.length > 1) {
        await teeSheetRulesAPI.createRule(tournamentId, {
          type: 'partner_group',
          players: group2Players,
          flexibility: rule.flexibility,
          priority: rule.priority,
          source: 'director_override',
          parameters: { splitFrom: rule._id }
        });
      }

      await fetchRules();
      setIsDetailOpen(false);
      setSelectedRule(null);
      setSuccessMessage(`Split group into ${group1Players.length > 1 ? '2 smaller groups' : 'a pair and individual'}`);
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      console.error('Error splitting group:', err);
      setError('Failed to split group');
    }
  };

  // Navigate to tee sheet
  const handleGenerateTeeSheet = () => {
    navigate(`/editor/${tournamentId}`);
  };

  // Initial load
  useEffect(() => {
    fetchRules();
    fetchConflicts();
    fetchPlayerCount();
  }, [fetchRules, fetchConflicts, fetchPlayerCount]);

  // Get group size config
  const getGroupSizeConfig = (playerCount) => {
    const configs = {
      1: { label: '1/4', color: 'gray', description: 'Single' },
      2: { label: '2/4', color: 'blue', description: 'Duo' },
      3: { label: '3/4', color: 'yellow', description: 'Trio' },
      4: { label: '4/4', color: 'green', description: 'Full' },
    };
    return configs[playerCount] || configs[1];
  };

  // Render individual rule card with expanded details
  const renderRuleCard = (rule) => {
    const typeConfig = RULE_TYPE_CONFIG[rule.type] || { icon: Sparkles, label: rule.type, color: 'gray' };
    const statusConfig = STATUS_CONFIG[rule.status] || { variant: 'secondary', label: rule.status };
    const flexConfig = FLEXIBILITY_CONFIG[rule.flexibility] || FLEXIBILITY_CONFIG.soft;
    const IconComponent = typeConfig.icon;
    const playerCount = rule.players?.length || 0;
    const groupSize = getGroupSizeConfig(playerCount);
    const isPartnerGroup = rule.type === 'partner_group';

    return (
      <div
        key={rule._id}
        className={cn(
          "rule-card-new",
          `rule-${typeConfig.color}`,
          `status-${rule.status}`,
          isPartnerGroup && `group-size-${groupSize.color}`
        )}
        onClick={() => handleOpenRuleDetail(rule)}
        role="button"
        tabIndex={0}
      >
        {/* Group Size Indicator (for partner groups) */}
        {isPartnerGroup && (
          <div className={cn("group-size-indicator", `size-${groupSize.color}`)}>
            <div className="size-dots">
              {[1, 2, 3, 4].map((slot) => (
                <span
                  key={slot}
                  className={cn("size-dot", slot <= playerCount ? "filled" : "empty")}
                />
              ))}
            </div>
            <span className="size-label">{groupSize.label}</span>
          </div>
        )}

        <div className="rule-card-body">
          <div className="rule-icon-col">
            <div className={cn("rule-icon-wrap", `icon-${typeConfig.color}`)}>
              <IconComponent className="h-4 w-4" />
            </div>
          </div>

          <div className="rule-main-col">
            <div className="rule-players-row">
              {isPartnerGroup ? (
                <div className="player-avatars">
                  {rule.playerNames?.slice(0, 4).map((name, idx) => (
                    <TooltipProvider key={idx}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="player-avatar">
                            {name.charAt(0)}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          <p>{name}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ))}
                  {playerCount > 4 && (
                    <div className="player-avatar more">+{playerCount - 4}</div>
                  )}
                </div>
              ) : (
                <span className="rule-players-text">{rule.playerNames?.join(', ') || 'All Players'}</span>
              )}
            </div>

            {isPartnerGroup && (
              <div className="player-names-full">
                {rule.playerNames?.join(' • ')}
              </div>
            )}

            {rule.parameters?.timeType && (
              <div className="rule-time-info">
                <Clock className="h-3 w-3" />
                <span>{rule.parameters.timeType}</span>
                {rule.parameters.targetTime && (
                  <span className="time-value">{rule.parameters.targetTime}</span>
                )}
              </div>
            )}

            {rule.originalRequest && (
              <div className="rule-request-preview">
                "{rule.originalRequest.slice(0, 50)}{rule.originalRequest.length > 50 ? '...' : ''}"
              </div>
            )}
          </div>

          <div className="rule-meta-col">
            <div className="rule-badges-stack">
              <Badge variant={flexConfig.variant} className="flex-badge-sm">
                {flexConfig.label}
              </Badge>
              {rule.source === 'director_override' && (
                <Badge variant="outline" className="override-badge-sm">
                  <Lock className="h-2.5 w-2.5" />
                </Badge>
              )}
            </div>
            <div className="priority-pill">P{rule.priority}</div>
          </div>

          <div className="rule-actions-col" onClick={(e) => e.stopPropagation()}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="action-trigger">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleOpenRuleDetail(rule)}>
                  <Edit2 className="h-4 w-4 mr-2" />
                  Edit Rule
                </DropdownMenuItem>
                {isPartnerGroup && playerCount > 2 && (
                  <DropdownMenuItem onClick={() => handleSplitGroup(rule)}>
                    <Scissors className="h-4 w-4 mr-2" />
                    Split Group
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => handleDeleteRule(rule._id)}
                  className="text-red-600"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Conflict Warning */}
        {rule.conflicts && rule.conflicts.filter(c => !c.resolved).length > 0 && (
          <div className="rule-conflict-bar">
            <AlertTriangle className="h-3 w-3" />
            <span>{rule.conflicts.filter(c => !c.resolved).length} conflict(s)</span>
          </div>
        )}

        {/* Full Group Banner */}
        {isPartnerGroup && playerCount === 4 && (
          <div className="full-group-banner">
            <Check className="h-3 w-3" />
            <span>Fills entire tee time</span>
          </div>
        )}
      </div>
    );
  };

  // Render rule type section
  const renderRuleTypeSection = (type, typeRules) => {
    if (!typeRules || typeRules.length === 0) return null;

    const typeConfig = RULE_TYPE_CONFIG[type] || { icon: Sparkles, label: type, color: 'gray', description: '' };
    const IconComponent = typeConfig.icon;

    return (
      <AccordionItem key={type} value={type} className={cn("rule-section", `type-${typeConfig.color}`)}>
        <AccordionTrigger className="rule-section-trigger">
          <div className="rule-section-header">
            <div className="section-title">
              <IconComponent className="h-5 w-5" />
              <span className="section-label">{typeConfig.label}</span>
              <Badge variant="secondary" className="rule-count-badge">
                {typeRules.length}
              </Badge>
            </div>
            <span className="section-description">{typeConfig.description}</span>
          </div>
        </AccordionTrigger>
        <AccordionContent className="rule-section-content">
          <div className="rules-grid">
            {typeRules.map(rule => renderRuleCard(rule))}
          </div>
        </AccordionContent>
      </AccordionItem>
    );
  };

  return (
    <div className="tournament-rules-page">
      {/* Page Header */}
      <div className="rules-page-header">
        <div className="header-left">
          <div className="header-title-row">
            <h1>Tee Sheet Rules</h1>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="sm" className="help-btn">
                    <HelpCircle className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="help-tooltip-content">
                  <div className="help-tooltip">
                    <strong>How Rules Work</strong>
                    <ol>
                      <li><strong>Extract Rules</strong> — Analyze player requests to create rules</li>
                      <li><strong>Review & Adjust</strong> — Click rules to edit priority and flexibility</li>
                      <li><strong>Resolve Conflicts</strong> — Fix any conflicting rules (shown in orange)</li>
                      <li><strong>Generate Tee Sheet</strong> — Apply rules to create optimal assignments</li>
                    </ol>
                    <p className="help-tip">
                      <Info className="h-3 w-3" />
                      Group sizes show how many slots each partner group fills (e.g., 3/4 = trio needing 1 more)
                    </p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <p className="header-subtitle">
            Extract and manage rules from player requests before generating the tee sheet
          </p>
        </div>
        <div className="header-actions">
          <Button
            variant="outline"
            onClick={handleExtractRules}
            disabled={isExtracting || playerCount === 0}
            className="extract-btn"
          >
            <Sparkles className={cn("h-4 w-4 mr-2", isExtracting && "animate-pulse")} />
            {isExtracting ? 'Extracting...' : 'Extract Rules'}
          </Button>
          <Button
            variant="ghost"
            onClick={() => { fetchRules(); fetchConflicts(); }}
            disabled={isLoading}
            title="Refresh rules"
          >
            <RefreshCw className={cn("h-4 w-4", isLoading && "animate-spin")} />
          </Button>
          <Button
            onClick={handleGenerateTeeSheet}
            disabled={stats.total === 0}
            className="generate-btn"
          >
            Generate Tee Sheet
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>

      {/* Messages */}
      {successMessage && (
        <div className="success-message">
          <Check className="h-4 w-4" />
          <span>{successMessage}</span>
        </div>
      )}

      {error && (
        <div className="error-message-full">
          <AlertCircle className="h-4 w-4" />
          <span>{error}</span>
          <Button variant="ghost" size="sm" onClick={() => setError(null)}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Stats Overview */}
      <div className="rules-stats-full">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="stat-card">
                <div className="stat-icon players">
                  <Users className="h-5 w-5" />
                </div>
                <div className="stat-info">
                  <span className="stat-value-lg">{playerCount}</span>
                  <span className="stat-label-lg">Players</span>
                </div>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>Total players in the tournament roster</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="stat-card">
                <div className="stat-icon total">
                  <FileText className="h-5 w-5" />
                </div>
                <div className="stat-info">
                  <span className="stat-value-lg">{stats.total}</span>
                  <span className="stat-label-lg">Total Rules</span>
                </div>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>All rules extracted from player requests</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="stat-card pending">
                <div className="stat-icon pending">
                  <Clock className="h-5 w-5" />
                </div>
                <div className="stat-info">
                  <span className="stat-value-lg">{stats.pending}</span>
                  <span className="stat-label-lg">Pending</span>
                </div>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>Rules waiting to be applied during tee sheet generation</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="stat-card applied">
                <div className="stat-icon applied">
                  <Check className="h-5 w-5" />
                </div>
                <div className="stat-info">
                  <span className="stat-value-lg">{stats.applied}</span>
                  <span className="stat-label-lg">Applied</span>
                </div>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>Rules successfully applied to the tee sheet</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {stats.conflicts > 0 && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="stat-card conflicts">
                  <div className="stat-icon conflicts">
                    <AlertTriangle className="h-5 w-5" />
                  </div>
                  <div className="stat-info">
                    <span className="stat-value-lg">{stats.conflicts}</span>
                    <span className="stat-label-lg">Conflicts</span>
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>Rules that conflict with each other - review before generating</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      {/* Conflicts Alert */}
      {conflicts.length > 0 && (
        <div className="conflicts-banner">
          <AlertTriangle className="h-5 w-5" />
          <div className="conflicts-info">
            <strong>{conflicts.length} rule conflict(s) detected</strong>
            <span>Some rules cannot be satisfied together. Review and resolve before generating tee sheet.</span>
          </div>
          <Button variant="outline" size="sm">
            Review Conflicts
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}

      {/* Main Content */}
      <div className="rules-main-content">
        {stats.total === 0 && !isLoading ? (
          <div className="empty-state-full">
            <div className="empty-icon">
              <Sparkles className="h-16 w-16" />
            </div>
            <h2>No Rules Extracted Yet</h2>
            <p>
              Extract rules from player requests to see partner groups, time preferences, and constraints.
            </p>
            {playerCount > 0 ? (
              <Button onClick={handleExtractRules} disabled={isExtracting} size="lg">
                <Sparkles className="h-5 w-5 mr-2" />
                Extract Rules from {playerCount} Players
              </Button>
            ) : (
              <div className="no-players-warning">
                <AlertCircle className="h-5 w-5" />
                <span>No players in roster. Add players first to extract rules.</span>
                <Button variant="outline" onClick={() => navigate(`/editor/${tournamentId}/roster`)}>
                  Go to Roster
                </Button>
              </div>
            )}
          </div>
        ) : (
          <Accordion
            type="multiple"
            value={expandedTypes}
            onValueChange={setExpandedTypes}
            className="rules-accordion-full"
          >
            {renderRuleTypeSection('partner_group', grouped.partnerGroups)}
            {renderRuleTypeSection('time_constraint', grouped.timeConstraints)}
            {renderRuleTypeSection('time_preference', grouped.timePreferences)}
            {renderRuleTypeSection('manual_override', grouped.manualOverrides)}
            {grouped.systemDefaults?.length > 0 && (
              <AccordionItem value="system_defaults" className="rule-section type-gray">
                <AccordionTrigger className="rule-section-trigger">
                  <div className="rule-section-header">
                    <div className="section-title">
                      <Settings className="h-5 w-5" />
                      <span className="section-label">System Defaults</span>
                      <Badge variant="secondary" className="rule-count-badge">
                        {grouped.systemDefaults.length}
                      </Badge>
                    </div>
                    <span className="section-description">Automatic sorting and balancing rules</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="rule-section-content">
                  <div className="rules-grid">
                    {grouped.systemDefaults.map(rule => renderRuleCard(rule))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            )}
          </Accordion>
        )}
      </div>

      {/* Bottom Action Bar */}
      {stats.total > 0 && (
        <div className="rules-action-bar">
          <div className="action-bar-info">
            <span>{stats.total} rules ready</span>
            {stats.conflicts > 0 && (
              <span className="conflict-warning">({stats.conflicts} conflicts)</span>
            )}
          </div>
          <Button onClick={handleGenerateTeeSheet} size="lg" className="generate-btn-lg">
            Generate Tee Sheet
            <ArrowRight className="h-5 w-5 ml-2" />
          </Button>
        </div>
      )}

      {/* Rule Detail Sheet */}
      <RuleDetailSheet
        rule={selectedRule}
        open={isDetailOpen}
        onOpenChange={setIsDetailOpen}
        onSave={handleSaveRule}
        onDelete={handleDeleteRule}
        onSplitGroup={handleSplitGroup}
      />
    </div>
  );
}

export default TournamentRules;
